import { type PropsWithChildren } from 'react';
import { useResolveResolutionDependentOption } from '@lingoda/student-frontend/router';
import { DixaVisibility } from '@lingoda/student-frontend/gtm/dixa';
import { HeaderContextInitializer } from '@lingoda/contexts';
import { AppFooter, Box, FramePageContainer, Suspense, dvh, styled } from '@lingoda/ui';
import { type RouteConfigOption, contactPath, resolveDynamicOption } from '@lingoda/router';
import { AppLanguageSelect } from '@lingoda/preferences';
import { AppBarGap, MobileBottomNavbar } from '@lingoda/student-navigation';
import routes from '../../../../routes';
import { PrivatePageHeader } from './PrivatePageHeader';
import { useHeaderBars } from './useHeaderBars';

type Props = PropsWithChildren<Optional<RouteConfigOption, 'tab'>>;

export const PrivatePageFrameWrapper = (props: Props) => {
    const {
        children,
        logoIsIcon = false,
        disableLogoLink = false,
        hideNavigation = false,
        headerIsPortal = false,
        hideMobileBottomNavbar,
    } = props;
    const pageNotScrollable = resolveDynamicOption(props.pageNotScrollable);
    const hideHeader = useResolveResolutionDependentOption(props.hideHeader);
    const hideFooter = resolveDynamicOption(props.hideFooter);
    const { wholeHeaderHeight, refs } = useHeaderBars();

    return (
        <HeaderContextInitializer height={wholeHeaderHeight} headerTheme="dark">
            <Box display="flex" minHeight={[dvh('100dvh')]} bgcolor={props.backgroundColor}>
                {!hideHeader && (
                    <PrivatePageHeader
                        logoIsIcon={logoIsIcon}
                        disableLogoLink={disableLogoLink}
                        headerIsPortal={headerIsPortal}
                        hideNavigation={hideNavigation}
                        refs={refs}
                    />
                )}

                <Box id="sidebar" />

                {/* `root-content` id required for embedding sticky bottom drawer via portal */}
                <PageWrapperBox id="root-content" scrollable={!pageNotScrollable}>
                    {!hideHeader && <AppBarGap />}
                    <PageBodyBox scrollable={!pageNotScrollable}>
                        <Suspense variant="linear">{children}</Suspense>
                    </PageBodyBox>
                    {!hideFooter && (
                        <FramePageContainer>
                            <AppFooter contactPath={contactPath()}>
                                <AppLanguageSelect />
                            </AppFooter>
                        </FramePageContainer>
                    )}
                </PageWrapperBox>
            </Box>

            {/* embed MobileBottomNavbar Portal to the `root` element to make sure */}
            {/* it's independent of any content inside descendant `root-content` div */}
            {!hideNavigation && !hideMobileBottomNavbar && (
                <MobileBottomNavbar container={document.getElementById('root')} routes={routes} />
            )}

            <DixaVisibility hidden={props.hideDixa} />
        </HeaderContextInitializer>
    );
};

interface WrapperProps {
    scrollable: boolean;
}

const PageWrapperBox = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'scrollable',
})<WrapperProps>(({ scrollable }) => ({
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    ...(!scrollable && { overflow: 'hidden' }),
}));

const PageBodyBox = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'scrollable',
})<WrapperProps>(({ scrollable }) => ({
    flex: '1 1 0',
    display: 'flex',
    flexDirection: 'column',
    ...(!scrollable && { overflow: 'hidden' }),
}));
